import { isSSR } from '@helpers/ssr';
import { applyMiddleware, compose, createStore, Store } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import reduxThunk from 'redux-thunk';
import rootReducer, { RootState } from './ducks';
import { exampleAppState, initialAppState } from './ducks/app/app.reducer';
import { exampleCompareState, initialCompareState } from './ducks/compare/compare.reducer';
import { examplePageContextState, initialPageContextState } from './ducks/pagecontext/pagecontext.reducer';
import { initialProductDetailState } from './ducks/productdetail/productdetail.reducer';

const composeEnhancers = composeWithDevTools || compose;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let store: Store<any> | undefined = undefined;

export default function getStore(reduxContext: ReduxContext | undefined | null, componentName: string): Store {
  if ((store === undefined && !isSSR && window.__REDUX_STORE) || (isSSR && reduxContext)) {
    const contextToUse = isSSR ? reduxContext : window.__REDUX_STORE;

    if (!contextToUse) {
      throw new Error('This should never happen, if it does then check store.ts and what context is being used');
    }

    // Create the initial state from the redux context and initial states from the reducers
    const initialState: Partial<RootState> = {
      app: {
        ...initialAppState,
        ...contextToUse.appState,
      },
      pageContext: {
        ...initialPageContextState,
        ...contextToUse.pageContext,
      },
      productDetail: {
        ...initialProductDetailState,
        ...contextToUse.productDetail,
      },
      compare: {
        ...initialCompareState,
      },
    };

    // Update the local store variable
    store = createStore(rootReducer, initialState, composeEnhancers(applyMiddleware(reduxThunk)));
  }

  if (store === undefined) {
    throw new Error(
      `If you're seeing this error you're trying to render ${
        componentName ?? 'undefined'
      } which requires redux and but hasn't been configured as WithRedux="true" on the ReactComponent user control. Or the root component you are trying to render isn't wrapped with 'withRedux(Component)'.\n\nIf you're in Storybook, you might need to wrap your component in a Fragment and also add <ProductDetails reduxContext={...}/> to initialise it. \r\n \r\n React Context Prop: \r\n ${JSON.stringify(
        reduxContext,
      )}`,
    );
  }

  // Return the store
  return store as Store;
}

export const exampleReduxStore: RootState = {
  app: exampleAppState,
  pageContext: examplePageContextState,
  productDetail: initialProductDetailState,
  compare: exampleCompareState,
};
